import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  fetchTaskCreateManualTaskZoneSetting,
  updateTaskCreateManualTaskZoneSetting,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { RequestPatchTaskCreateManualTaskZoneSetting, TaskCreateManualTaskZoneSettingMember } from '../../../../states/types/task'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "表示する",
    value: true,
  },
  {
    label: "表示しない",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "表示する",
    value: "on",
  },
  {
    label: "表示しない",
    value: "off",
  },
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isDisplayCreateManualTaskZone, setIsDisplayCreateManualTaskZone] = useState<boolean>(false)

  const [originMemberList, setOriginMemberList] = useState<TaskCreateManualTaskZoneSettingMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskCreateManualTaskZoneSettingMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeDisplay = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskCreateManualTaskZoneSetting = { change_common_setting: data.commonFlg }
    const display = buildParam(data.type)
    if (data.commonFlg) {
      param.display = display ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = display === null
      if (data.type !== "common") {
        param.display = display ?? true
      }
    }
    dispatch(updateTaskCreateManualTaskZoneSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(display ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, display: param.display ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskCreateManualTaskZoneSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.display)
        setIsDisplayCreateManualTaskZone(result.common.display)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_CREATE_MANUAL_TASK_ZONE_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 業務仕分けボードの作業データ手動登録可能時間を本人以外も表示`
            ) : (
              `Qasee - 工数管理 - 業務仕分けボードの作業データ手動登録可能時間を本人以外も表示`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_CREATE_MANUAL_TASK_ZONE_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        業務仕分けボードの作業データ手動登録可能時間を本人以外も表示
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          業務仕分けボードの作業データ手動登録可能時間を本人以外も表示
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>業務仕分けボードの作業データ手動登録可能時間を本人以外も表示できるようにします。</p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              情報を取得しています…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${isDisplayCreateManualTaskZone ? " selected" : ""}`}
                        onClick={() => setIsDisplayCreateManualTaskZone(!isDisplayCreateManualTaskZone)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${isDisplayCreateManualTaskZone ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${isDisplayCreateManualTaskZone ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            業務仕分けボードの作業データ手動登録可能時間を本人以外も表示する
                          </div>
                          <div className="check_box_description">
                            業務仕分けボードの作業データ手動登録可能時間を本人以外も表示できるようにします
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeDisplay({ commonFlg: true, type: isDisplayCreateManualTaskZone ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.display }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="業務仕分けボードの作業データ手動登録可能時間を本人以外も表示する"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.display) }))}
              selectList={SELECT_LIST}
              label="業務仕分けボードの作業データ手動登録可能時間を本人以外も表示する"
              prefix="業務仕分けボードの作業データ手動登録可能時間を本人以外も"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeDisplay(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
